import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface CategoryDisplaySettings {
  logo_size: string;
  title_size: string;
  title_font: string;
  title_bold: boolean;
  logo_scale: number;
  title_scale: number;
  use_numeric: boolean;
  // Mobile-specific settings
  mobile_logo_scale: number;
  mobile_title_scale: number;
  // New px-based settings for desktop
  desktop_font_size: number;
  desktop_icon_size: number;
  desktop_padding: number;
  desktop_border_radius: number;
  // New px-based settings for mobile
  mobile_font_size: number;
  mobile_icon_size: number;
  mobile_padding: number;
  mobile_border_radius: number;
}

const DEFAULT_CATEGORY_DISPLAY: CategoryDisplaySettings = {
  logo_size: 'medium',
  title_size: 'medium',
  title_font: 'default',
  title_bold: false,
  logo_scale: 1.0,
  title_scale: 1.0,
  use_numeric: false,
  mobile_logo_scale: 0.8,
  mobile_title_scale: 0.8,
  // Default px values
  desktop_font_size: 12,
  desktop_icon_size: 20,
  desktop_padding: 16,
  desktop_border_radius: 9999,
  mobile_font_size: 11,
  mobile_icon_size: 16,
  mobile_padding: 12,
  mobile_border_radius: 9999,
};

export const FONT_OPTIONS = [
  { value: 'default', label: 'Default (System)', fontFamily: '' },
  { value: 'inter', label: 'Inter', fontFamily: 'Inter, sans-serif' },
  { value: 'poppins', label: 'Poppins', fontFamily: 'Poppins, sans-serif' },
  { value: 'montserrat', label: 'Montserrat', fontFamily: 'Montserrat, sans-serif' },
  { value: 'oswald', label: 'Oswald', fontFamily: 'Oswald, sans-serif' },
  { value: 'roboto-condensed', label: 'Roboto Condensed', fontFamily: '"Roboto Condensed", sans-serif' },
  { value: 'bebas', label: 'Bebas Neue', fontFamily: '"Bebas Neue", sans-serif' },
  { value: 'russo', label: 'Russo One', fontFamily: '"Russo One", sans-serif' },
  { value: 'teko', label: 'Teko', fontFamily: 'Teko, sans-serif' },
  { value: 'rajdhani', label: 'Rajdhani', fontFamily: 'Rajdhani, sans-serif' },
];

export function getFontFamily(fontValue: string): string {
  const font = FONT_OPTIONS.find(f => f.value === fontValue);
  return font?.fontFamily || '';
}

export function useCategoryDisplaySettings() {
  return useQuery({
    queryKey: ['site-settings', 'category_display'],
    queryFn: async (): Promise<CategoryDisplaySettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'category_display')
        .single();
      
      if (error || !data) {
        return DEFAULT_CATEGORY_DISPLAY;
      }
      
      return {
        ...DEFAULT_CATEGORY_DISPLAY,
        ...(data.value as object),
      };
    },
    staleTime: 30000, // Cache for 30 seconds
  });
}

export function useUpdateCategoryDisplaySettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: CategoryDisplaySettings) => {
      // Try to update first
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'category_display')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'category_display');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'category_display', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'category_display'] });
    },
  });
}

// Match settings
interface MatchSettings {
  auto_start_on_click: boolean;
}

const DEFAULT_MATCH_SETTINGS: MatchSettings = {
  auto_start_on_click: true,
};

export function useMatchSettings() {
  return useQuery({
    queryKey: ['site-settings', 'match_settings'],
    queryFn: async (): Promise<MatchSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'match_settings')
        .single();
      
      if (error || !data) {
        return DEFAULT_MATCH_SETTINGS;
      }
      
      return {
        ...DEFAULT_MATCH_SETTINGS,
        ...(data.value as object),
      };
    },
    staleTime: 10000, // Cache for 10 seconds
  });
}

export function useUpdateMatchSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: MatchSettings) => {
      // Try to update first
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'match_settings')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'match_settings');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'match_settings', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'match_settings'] });
    },
  });
}

// Channel Setup settings - controls Select Stream visibility in Streamer Dashboard
interface ChannelSetupSettings {
  select_stream_enabled: boolean;      // Toggle A: Streamer Dashboard – Select Stream Visibility
  channel_system_enabled: boolean;     // Toggle B: Channel System Master Switch
}

const DEFAULT_CHANNEL_SETUP: ChannelSetupSettings = {
  select_stream_enabled: true,
  channel_system_enabled: true,
};

export function useChannelSetupSettings() {
  return useQuery({
    queryKey: ['site-settings', 'channel_setup'],
    queryFn: async (): Promise<ChannelSetupSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'channel_setup')
        .single();
      
      if (error || !data) {
        return DEFAULT_CHANNEL_SETUP;
      }
      
      return {
        ...DEFAULT_CHANNEL_SETUP,
        ...(data.value as object),
      };
    },
    staleTime: 10000,
  });
}

export function useUpdateChannelSetupSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: ChannelSetupSettings) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'channel_setup')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'channel_setup');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'channel_setup', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'channel_setup'] });
    },
  });
}

// Stream Health settings for CS page
interface StreamHealthSettings {
  show_stream_health: boolean;
  show_streamer_dashboard_health: boolean;  // New toggle for streamer dashboard
}

const DEFAULT_STREAM_HEALTH: StreamHealthSettings = {
  show_stream_health: true,
  show_streamer_dashboard_health: true,
};

export function useStreamHealthSettings() {
  return useQuery({
    queryKey: ['site-settings', 'stream_health'],
    queryFn: async (): Promise<StreamHealthSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'stream_health')
        .single();
      
      if (error || !data) {
        return DEFAULT_STREAM_HEALTH;
      }
      
      return {
        ...DEFAULT_STREAM_HEALTH,
        ...(data.value as object),
      };
    },
    staleTime: 10000,
  });
}

export function useUpdateStreamHealthSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: StreamHealthSettings) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'stream_health')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'stream_health');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'stream_health', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'stream_health'] });
    },
  });
}

// Auto HTTPS Stream URL Fix settings
interface AutoHttpsSettings {
  auto_https_enabled: boolean;
}

const DEFAULT_AUTO_HTTPS: AutoHttpsSettings = {
  auto_https_enabled: true,
};

export function useAutoHttpsSettings() {
  return useQuery({
    queryKey: ['site-settings', 'auto_https'],
    queryFn: async (): Promise<AutoHttpsSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'auto_https')
        .single();
      
      if (error || !data) {
        return DEFAULT_AUTO_HTTPS;
      }
      
      return {
        ...DEFAULT_AUTO_HTTPS,
        ...(data.value as object),
      };
    },
    staleTime: 10000,
  });
}

export function useUpdateAutoHttpsSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: AutoHttpsSettings) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'auto_https')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'auto_https');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'auto_https', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'auto_https'] });
    },
  });
}

// Utility function to convert http to https
export function autoFixHttpsUrl(url: string, autoHttpsEnabled: boolean): string {
  if (!autoHttpsEnabled || !url) return url;
  if (url.toLowerCase().startsWith('http://')) {
    return 'https://' + url.substring(7);
  }
  return url;
}

// Event Match Settings - controls visibility of Add Event Match button
interface EventMatchSettings {
  event_match_enabled: boolean;
}

const DEFAULT_EVENT_MATCH: EventMatchSettings = {
  event_match_enabled: false,
};

export function useEventMatchSettings() {
  return useQuery({
    queryKey: ['site-settings', 'event_match'],
    queryFn: async (): Promise<EventMatchSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'event_match')
        .single();
      
      if (error || !data) {
        return DEFAULT_EVENT_MATCH;
      }
      
      return {
        ...DEFAULT_EVENT_MATCH,
        ...(data.value as object),
      };
    },
    staleTime: 10000,
  });
}

export function useUpdateEventMatchSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: EventMatchSettings) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'event_match')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'event_match');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'event_match', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'event_match'] });
    },
  });
}

// Category Landing Page settings
interface CategoryLandingSettings {
  landing_page_enabled: boolean;
  landing_display_mode: 'logo' | 'name'; // 'logo' = site logo, 'name' = category slug/name text
}

const DEFAULT_CATEGORY_LANDING: CategoryLandingSettings = {
  landing_page_enabled: false,
  landing_display_mode: 'logo',
};

export function useCategoryLandingSettings() {
  return useQuery({
    queryKey: ['site-settings', 'category_landing'],
    queryFn: async (): Promise<CategoryLandingSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'category_landing')
        .single();
      
      if (error || !data) {
        return DEFAULT_CATEGORY_LANDING;
      }
      
      return {
        ...DEFAULT_CATEGORY_LANDING,
        ...(data.value as object),
      };
    },
    staleTime: 10000,
  });
}

export function useUpdateCategoryLandingSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: CategoryLandingSettings) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'category_landing')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'category_landing');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'category_landing', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'category_landing'] });
    },
  });
}

// Gemini API Configuration settings
export interface GeminiApiKey {
  id: string;
  key: string;
  enabled: boolean;
  status: 'untested' | 'valid' | 'invalid';
  lastTested?: string;
}

export type GeminiModel = 'gemini-2.5-flash' | 'gemini-2.5-flash-lite';

export interface GeminiApiSettings {
  apiKeys: GeminiApiKey[];
  defaultModel: GeminiModel;
}

const DEFAULT_GEMINI_SETTINGS: GeminiApiSettings = {
  apiKeys: [],
  defaultModel: 'gemini-2.5-flash',
};

export function useGeminiApiSettings() {
  return useQuery({
    queryKey: ['site-settings', 'gemini_api'],
    queryFn: async (): Promise<GeminiApiSettings> => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'gemini_api')
        .single();
      
      if (error || !data) {
        return DEFAULT_GEMINI_SETTINGS;
      }
      
      return {
        ...DEFAULT_GEMINI_SETTINGS,
        ...(data.value as object),
      };
    },
    staleTime: 10000,
  });
}

export function useUpdateGeminiApiSettings() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (settings: GeminiApiSettings) => {
      const { data: existing } = await supabase
        .from('site_settings')
        .select('id')
        .eq('key', 'gemini_api')
        .single();
      
      if (existing) {
        const { error } = await supabase
          .from('site_settings')
          .update({ 
            value: settings as any,
            updated_at: new Date().toISOString()
          })
          .eq('key', 'gemini_api');
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('site_settings')
          .insert([{ 
            key: 'gemini_api', 
            value: settings as any
          }]);
        
        if (error) throw error;
      }
      
      return settings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-settings', 'gemini_api'] });
    },
  });
}
